import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { combineLatest } from "rxjs";
import { first, mergeMap } from "rxjs/operators";
import { Constants } from "src/app/interfaces/constants";
import { selectCurrentUser } from "src/app/user/state";
import { SubSink } from "subsink";
import {
  AbstractUserService,
  IDefaultStepPayloadData,
  IPerson,
  Step
} from "../../interfaces";
import { LogClass, Logger, LogMethod } from "../../logger";
import { NavigationService } from "../../services";
import { StepFacade } from "../services/step.facade";

@Component({
    selector: "circ-forward",
    templateUrl: "./forward.component.html",
    styleUrls: ["./forward.component.scss"],
    standalone: false
})
@LogClass({ source: "ForwardComponent" })
export class ForwardComponent implements OnInit, OnDestroy {
  public form = new FormGroup({
    responsible: new FormControl<IPerson | null>(null, [Validators.required]),
    comment: new FormControl("", [])
  });

  public loading = true;
  public step: Step;
  public title: string;

  private logger: Logger;
  private subscriptions = new SubSink();

  constructor(
    private stepFacade: StepFacade,
    private userService: AbstractUserService,
    private store: Store,
    private navigate: NavigationService,
    private route: ActivatedRoute
  ) {
    this.logger.debug("ctor");
  }

  public abort(): void {
    this.navigate.back();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @LogMethod()
  public ngOnInit(): void {
    this.route.paramMap.pipe(first()).subscribe((params) => {
      const circId = params.get(Constants.routeParams.circId);
      const stepId = params.get(Constants.routeParams.stepId);
      this.logger.info("id:" + circId + " - " + stepId);
      combineLatest([
        this.stepFacade.getStepsForCirculation(circId).pipe(first()),
        this.store.select(selectCurrentUser).pipe(first())
      ])
        .pipe(first())
        .subscribe(
          ([s, u]) => {
            this.logger.info("loaded " + s.length + " steps");
            // tslint:disable-next-line: triple-equals - params are always string - id could be int...
            const step = s.find((x) => x.id == stepId);
            if (!step) {
              this.logger.error(`No step id:${stepId} found on circ:${circId}`);
              this.navigate.toError(`Step not found: ${circId} / ${stepId} `);
              this.loading = false;
              return;
            }

            if (
              step.props.status !== "InProgress" ||
              !step.props.tasks.assignees.some((x) => x.user.id == u.id)
            ) {
              // not allowed to forward - how did we get here?!
              this.navigate.toError(
                `Step can not be forwarded: ${circId} / ${stepId} `
              );
            }

            this.step = step;
            const data = step.props.payload?.data as IDefaultStepPayloadData;
            this.title = data?.title;
            this.loading = false;
          },
          (err) => {
            this.logger.error("ngOnInit", err);
            this.navigate.toError(err);
          }
        );
    });
  }

  public save(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    this.loading = true;
    this.logger.info("saving");

    this.userService
      .getUsers([this.form.value.responsible.login])
      .pipe(
        mergeMap(([user]) => {
          return this.stepFacade.forward({
            step: this.step.props,
            forwardTo: user,
            comment: this.form.value.comment
          });
        }),
        first()
      )
      .subscribe(
        () => {
          this.logger.info("saved.");
          this.navigate.toCirculation(this.step.props.circulationId);
        },
        (err) => {
          this.logger.error("save", err);
          this.navigate.toError(err);
        }
      );
  }
}
